"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {cn} from "@/lib/utils";
import {motion} from "framer-motion";

const Popover = ({
  children,
  open = false,
  setOpen,
  onOpenChange,
  modal = false,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
}) => {
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        open &&
        contentRef.current &&
        !contentRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    window.__flowBuilderIframeDoc.addEventListener(
      "mousedown",
      handleClickOutside,
    );
    return () =>
      window.__flowBuilderIframeDoc.removeEventListener(
        "mousedown",
        handleClickOutside,
      );
  }, [open]);

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={v => {
        setOpen?.(v);
        onOpenChange?.(v);
      }}
      modal={modal}
    >
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) return child;

        if (child.type === PopoverTrigger) {
          return React.cloneElement(child as React.ReactElement<any>, {
            ref: triggerRef,
          });
        }

        if (child.type === PopoverContent) {
          return React.cloneElement(child as React.ReactElement<any>, {
            ref: contentRef,
          });
        }

        return child;
      })}
    </PopoverPrimitive.Root>
  );
};

const PopoverTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>((props, ref) => <PopoverPrimitive.Trigger {...props} ref={ref} />);
PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName;

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({className, align = "center", sideOffset = 4, ...props}, ref) => {
  const popoverContainer = window.__flowBuilderIframeDoc.body;

  return (
    <PopoverPrimitive.Portal container={popoverContainer}>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-50 rounded-xl border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className,
        )}
        {...props}
      >
        <motion.div
          initial={{opacity: 0, y: 10}}
          animate={{opacity: 1, y: 0}}
          exit={{opacity: 0, y: 10}}
          transition={{duration: 0.2}}
        >
          {props.children}
        </motion.div>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export {Popover, PopoverTrigger, PopoverContent, PopoverAnchor};
